import type { RulebookLevelRangeKind } from "@next-platform/client-common-core";
import { formatMeterLevelRange, isMeterLevelRevealed, renderRulebookUserText, resolveReadableTextColor } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import type { PublicMeterLevel } from "@next-platform/packages-ts-types";
import type { CSSProperties } from "react";
import React, { useMemo } from "react";

import Trans from "@/components/Trans/Trans";

import { HUD_CURRENT_ROW, HUD_MUTED } from "./hudClassNames";

type RulebookLevelListProps = {
  levels: PublicMeterLevel[];
  currentLevelIndex: number;
  revealedIndex?: number;
  rangeKind?: RulebookLevelRangeKind;
  /** Meter color for the "You're here" pill. */
  meterColor?: string;
  characterName?: string;
  /** Prefix ranges with the level number ("Lv 2 · 6–99"), for counters shown as levels. */
  showLevelNumber?: boolean;
  "data-testid"?: string;
};

type LevelRangeLabelProps = {
  level: PublicMeterLevel;
  levelIndex: number;
  rangeKind: RulebookLevelRangeKind;
  showLevelNumber: boolean;
};

const LevelRangeLabel = ({ level, levelIndex, rangeKind, showLevelNumber }: LevelRangeLabelProps) => {
  const rangeValues = useMemo(
    () => ({ range: formatMeterLevelRange({ level, kind: rangeKind }), number: levelIndex + 1, from: level.from }),
    [level, levelIndex, rangeKind],
  );

  if (showLevelNumber) {
    return <Trans i18nKey="chat:rulebook.hud.sheet.levelNumberRange" defaults="Lv {{number}} · {{range}}" values={rangeValues} />;
  }
  if (rangeKind !== "days") {
    return <>{formatMeterLevelRange({ level, kind: rangeKind })}</>;
  }
  if (level.to !== null && level.from === level.to) {
    return <Trans i18nKey="chat:rulebook.hud.sheet.dayRangeSingle" defaults="Day {{from}}" values={rangeValues} />;
  }
  return (
    <>
      {level.to === null ? (
        <Trans i18nKey="chat:rulebook.hud.sheet.dayRangeOpen" defaults="Day {{range}}" values={rangeValues} />
      ) : (
        <Trans i18nKey="chat:rulebook.hud.sheet.daysRange" defaults="Days {{range}}" values={rangeValues} />
      )}
    </>
  );
};

type LevelRowProps = {
  level: PublicMeterLevel;
  levelIndex: number;
  isCurrent: boolean;
  isRevealed: boolean;
  rangeKind: RulebookLevelRangeKind;
  showLevelNumber: boolean;
  pillStyle?: CSSProperties;
  characterName?: string;
  testId: string;
};

const CAPTION_SEPARATOR = " · ";

/** "Polite · Polite, a little reserved": the level label plus its status line when it has one. */
const levelText = ({ level, characterName }: { level: PublicMeterLevel; characterName?: string }): string => {
  const label = level.label !== undefined ? renderRulebookUserText({ text: level.label, characterName }) : "";
  const caption = level.caption ? renderRulebookUserText({ text: level.caption, characterName }) : "";
  return caption ? label + CAPTION_SEPARATOR + caption : label;
};

const LevelRow = ({ level, levelIndex, isCurrent, isRevealed, rangeKind, showLevelNumber, pillStyle, characterName, testId }: LevelRowProps) => (
  <div
    data-testid={`${testId}-Level-${levelIndex}`}
    data-current={isCurrent ? "true" : undefined}
    className={`flex items-center gap-2.5 rounded-[8px] border px-2.5 py-[7px] ${isCurrent ? HUD_CURRENT_ROW : "border-transparent"}`}
  >
    <span data-testid={`${testId}-Level-${levelIndex}-Range`} className={`min-w-[52px] flex-none whitespace-nowrap text-[11px] ${HUD_MUTED}`}>
      <LevelRangeLabel level={level} levelIndex={levelIndex} rangeKind={rangeKind} showLevelNumber={showLevelNumber} />
    </span>
    <span className={`flex min-w-0 flex-1 items-center gap-1.5 text-[13px] ${isCurrent ? "font-semibold" : ""} ${isRevealed ? "" : HUD_MUTED}`}>
      {isRevealed ? (
        levelText({ level, characterName })
      ) : (
        <>
          <Icon icon={IconVariants.LOCK} size={IconSize.XSMALL} />
          <Trans i18nKey="chat:rulebook.hud.sheet.hiddenLevel" defaults="???" />
        </>
      )}
    </span>
    {isCurrent ? (
      <span
        data-testid={`${testId}-YouAreHere`}
        className={`rounded-full px-[7px] py-[2px] text-[10px] font-bold ${pillStyle === undefined ? "text-white" : ""}`}
        style={pillStyle}
      >
        <Trans i18nKey="chat:rulebook.hud.sheet.youreHere" defaults="You're here" />
      </span>
    ) : null}
  </div>
);

const RulebookLevelList = ({
  levels,
  currentLevelIndex,
  revealedIndex,
  rangeKind = "bounds",
  meterColor,
  characterName,
  showLevelNumber = false,
  "data-testid": testId = "RulebookLevelList",
}: RulebookLevelListProps) => {
  const pillStyle = useMemo<CSSProperties | undefined>(
    () => (meterColor ? { backgroundColor: meterColor, color: resolveReadableTextColor({ backgroundHex: meterColor }) } : undefined),
    [meterColor],
  );

  return (
    <div data-testid={testId} className="flex flex-col gap-1">
      {levels.map((level, levelIndex) => (
        <LevelRow
          key={levelIndex}
          level={level}
          levelIndex={levelIndex}
          isCurrent={levelIndex === currentLevelIndex}
          isRevealed={level.label !== undefined && (levelIndex === currentLevelIndex || isMeterLevelRevealed({ level, levelIndex, revealedIndex }))}
          rangeKind={rangeKind}
          showLevelNumber={showLevelNumber}
          pillStyle={pillStyle}
          characterName={characterName}
          testId={testId}
        />
      ))}
    </div>
  );
};

export default React.memo(RulebookLevelList);
